import * as E from 'fp-ts/lib/Either'
import * as O from 'fp-ts/lib/Option'
import { pipe } from 'fp-ts/lib/pipeable'
import { sequenceT } from 'fp-ts/lib/Apply'

import {
  AP,
  arrayV,
  eurosInRangeV,
  gte,
  metaPath,
  required,
  Validator,
  ValidationError
} from '@espoon-voltti/ts-libs/lib/validation'

import { maybeFormikErrors } from '@espoon-voltti/react-voltti/components/form/formik-util'
import { AttachmentModel } from '@espoon-voltti/react-voltti/components/attachment-selector/domain/model'

import { AgeDistribution } from '@espoon-voltti/nuora-domain/grants'

import {
  ProjectGrantDraftApp,
  ProjectGrantFormData,
  ValidDraftApp
} from '../../../domain'

import { nuoraOrgV } from './applicant'
import { eventV } from './event'
import { eurosMax, eurosMin } from './constants'
import { transactionsV } from './transactions'

export * from './constants'
export * from './general-grant'

export const getAppErrors = (app: ProjectGrantDraftApp) =>
  pipe(projectGrantDraftAppV(app), maybeFormikErrors, O.toUndefined)

export const maybeValidApp = (
  app: ProjectGrantDraftApp
): O.Option<ValidDraftApp> => pipe(projectGrantDraftAppV(app), O.fromEither)

const projectGrantDraftAppV: Validator<ProjectGrantDraftApp, ValidDraftApp> = (
  x
) => {
  return pipe(
    sequenceT(AP)(
      nuoraOrgV(['applicant', 'nuoraOrg'])(x.applicant.nuoraOrg),
      formV(['form'])(x.form)
    ),
    E.map(([nuoraOrg, form]) => ({
      id: x.id,
      grantPeriodId: x.grantPeriodId,
      state: x.state,
      form,
      applicant: {
        ...x.applicant,
        nuoraOrg
      }
    }))
  )
}

const formV =
  (
    path: ReadonlyArray<string>
  ): Validator<ProjectGrantFormData, ProjectGrantFormData> =>
  (x) =>
    pipe(
      sequenceT(AP)(
        eventV([...path, 'event'])(x.event),
        eurosInRangeV(
          [...path, 'appliedSum'],
          eurosMin,
          eurosMax
        )(x.appliedSum),
        required([...path, 'purpose'])(x.purpose),
        participantsV([...path, 'participants'])(x.participants),
        transactionsV([...path, 'expenses'])(x.expenses),
        transactionsV([...path, 'incomes'])(x.incomes),
        arrayV([...path, 'attachments'], attachmentV)(x.attachments)
      ),
      E.map((_) => x)
    )

const participantsV =
  (path: ReadonlyArray<string>): Validator<AgeDistribution, AgeDistribution> =>
  (x) =>
    pipe(
      x.ages7to20 + x.ages21to28 + x.ages29andOver,
      gte(metaPath(path), 1),
      E.map((_) => x)
    )

const attachmentV =
  (path: ReadonlyArray<string>): Validator<AttachmentModel, AttachmentModel> =>
  (x) => {
    switch (x.kind) {
      case 'UploadedAttachment':
      case 'PublishedAttachment':
        return E.right(x)
      case 'LoadingAttachment':
      case 'ErroredAttachment':
        return E.left([new ValidationError(path, x.kind, { type: x.kind })])
      default:
        const _: never = x
        return _
    }
  }
